import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, X, Heart, User, LogOut, Settings, LayoutDashboard, Mail, ChevronDown, Ticket, Wallet } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import CartButton from "@/components/CartButton";

// Official Blue vs Blue Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

const mainNavLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/events", label: "Events" },
  { href: "/community", label: "Community" },
  { href: "/podcast", label: "Podcast" },
  { href: "/live", label: "Live" },
  { href: "/conference", label: "Conference" },
  { href: "/gallery", label: "Gallery" },
];

const moreNavLinks = [
  { href: "/campaigns", label: "Campaigns" },
  { href: "/get-involved", label: "Get Involved" },
  { href: "/contact", label: "Contact" },
];

const allNavLinks = [...mainNavLinks, ...moreNavLinks];

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const isAdmin = user && ['master_admin', 'super_admin', 'developer', 'admin', 'event_manager', 'blog_editor', 'donation_manager', 'ticket_scanner'].includes(user.role);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-lg supports-[backdrop-filter]:bg-white/60">
      <div className="container-custom">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src={LOGO_URL} 
              alt="Blue vs Blue" 
              className="h-12 w-auto rounded"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-5">
            {mainNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`nav-link text-sm ${isActive(link.href) ? 'text-deep-blue font-semibold' : ''}`}
              >
                {link.label}
              </Link>
            ))}
            {/* More dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="nav-link text-sm flex items-center gap-1 outline-none">
                More <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {moreNavLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link to={link.href} className={`w-full ${isActive(link.href) ? 'font-semibold' : ''}`} data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side buttons */}
          <div className="flex items-center gap-4">
            <Link to="/donate" data-testid="nav-donate-btn">
              <Button className="btn-primary hidden sm:flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Donate
              </Button>
            </Link>

            {user ? (
              <div className="flex items-center gap-2">
                <CartButton />
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                      {user.picture ? (
                        <img src={user.picture.startsWith('/api') ? `${API.replace('/api', '')}${user.picture}` : user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-deep-blue text-white flex items-center justify-center text-sm font-semibold">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="hidden md:inline text-sm font-medium">{user.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5 text-sm text-slate-500">
                      {user.email}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2" data-testid="user-dashboard-link">
                        <Ticket className="w-4 h-4" />
                        My Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-wallet" className="flex items-center gap-2" data-testid="wallet-link">
                        <Wallet className="w-4 h-4" />
                        My Wallet
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dm" className="flex items-center gap-2" data-testid="dm-link">
                        <Mail className="w-4 h-4" />
                        Messages
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/inbox" className="flex items-center gap-2" data-testid="inbox-link">
                        <Mail className="w-4 h-4" />
                        Inbox
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-dashboard-link">
                            <LayoutDashboard className="w-4 h-4" />
                            Admin Dashboard
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-red-600" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login-btn">
                <Button variant="outline" className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile menu button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] overflow-y-auto">
                <nav className="flex flex-col gap-1 mt-6">
                  {allNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className={`text-base font-medium px-3 py-3 rounded-lg ${isActive(link.href) ? 'text-deep-blue bg-deep-blue/5' : 'text-slate-700'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
                  
                  {user && (
                    <>
                      <div className="border-t border-slate-200 mt-3 pt-3">
                        <span className="text-xs text-slate-400 px-3 uppercase tracking-wide">My Account</span>
                      </div>
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="text-base font-medium px-3 py-3 rounded-lg text-slate-700">
                        My Dashboard
                      </Link>
                      <Link to="/my-wallet" onClick={() => setMobileOpen(false)} className="text-base font-medium px-3 py-3 rounded-lg text-slate-700">
                        My Wallet
                      </Link>
                      <Link to="/profile" onClick={() => setMobileOpen(false)} className="text-base font-medium px-3 py-3 rounded-lg text-slate-700">
                        My Profile
                      </Link>
                      {(user.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin") && (
                        <Link to="/admin/dashboard" onClick={() => setMobileOpen(false)} className="text-base font-medium px-3 py-3 rounded-lg text-deep-blue">
                          Admin Dashboard
                        </Link>
                      )}
                    </>
                  )}
                  
                  <div className="border-t border-slate-200 pt-4 mt-3 space-y-3 px-1">
                    <Link to="/donate" onClick={() => setMobileOpen(false)}>
                      <Button className="btn-primary w-full py-3 text-base">
                        <Heart className="w-4 h-4 mr-2" />
                        Donate Now
                      </Button>
                    </Link>
                    {user ? (
                      <Button variant="outline" className="w-full py-3 text-base" onClick={() => { logout(); setMobileOpen(false); }}>
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                      </Button>
                    ) : (
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        <Button variant="outline" className="w-full py-3 text-base">
                          <User className="w-4 h-4 mr-2" />
                          Sign In
                        </Button>
                      </Link>
                    )}
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
